<template>
  <div class="pb-player">
    <div class="player-shadow" @click="showFutureWarning" v-if="isFuture">
    </div>
    <component
      :is="type"
      id="vvvv"
      preload="none"
      :crossorigin="crossorigin"
      ref="vjsPlayer"
      class="video-js"
    ></component>
  </div>
</template>

<script>
import playerjs from "player.js";
import videojs from "video.js";
import { mapState } from "vuex";
import { MUTATION_UPDATE } from "@/store/mutations";
import { ACTION_NEXT_TRACK, ACTION_PREVIOUS_TRACK } from "@/store/actions";
import { postEvent, postGetterCallback, setDocumentTitle, sleep} from '@/common/utils';
import { getTranscript, reportEngagement } from "@/common/api";

var domRef = videojs.extend(videojs.getComponent("Component"));
domRef.prototype.createEl = function createEl() {
  return this.options_.dom;
};

videojs.registerComponent("DomRef", domRef);
videojs.addLanguage('en', {"The media could not be loaded, either because the server or network failed or because the format is not supported.": "Error opening file. "});

export default {
  name: "PbPlayer",
  props: {
    options: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * @var {Components.Schemas.PlayerEpisodeItemDTO} episode
     */
    episode: Object,
    startAt: Number,
    type: String,
    crossorigin: String
  },
  watch: {
    ["options.sources"](newValue) {
      this.player.src(newValue);
      this.player.play();
      this.engagementStart = null;
      this.engagementEnd = null;
      this.retry = 1;
    },
    start(){
      this.player.play();
    },
    stop(){
      this.player.pause();
    },
    playbackRate(newValue){
      if (this.player){
        this.player.playbackRate(newValue);
      }
    },
    episode(newValue){
      this.initEpisode(newValue);
    }
  },
  computed: {
    ...mapState(["setting", "episodes", "current",'start','stop','playbackRate']),
    isFuture(){
      return this.episode.resource == 'future episode';
    }
  },
  data() {
    return {
      player: null,
      startAtFix: false,
      initFFT: false,
      analyser: null,
      engagementStart: null,
      engagementEnd: null,
      t:0,
      retry:1,
    };
  },
  mounted() {


    this.player = videojs(this.$refs.vjsPlayer, this.options, async () => {

      const adapter = new playerjs.VideoJSAdapter(this.player);

      this.postEvent("PB.Widget.Events.READY");
      this.$emit("ready");

      this.initEpisode(this.episode);

      adapter.ready();
    });

    this.$store.commit(MUTATION_UPDATE,["playerError",false]);


    if ("mediaSession" in navigator) {
      navigator.mediaSession.setActionHandler("seekbackward", () => {
        this.addCurrentTime(-15);
      });
      navigator.mediaSession.setActionHandler("seekforward", () => {
        this.addCurrentTime(+15);
      });
    }

    this.player.on('timeupdate',this.timeupdate);
    this.player.on('progress',this.progress);
    this.player.on('playing',this.playing);
    this.player.on('ended',this.ended);
    this.player.on('seeked',this.seeked);
    this.player.on('loadeddata',this.loadeddata);

    this.player.on("waiting", this.waiting);
    // this.player.on("loadstart", this.waiting);
    this.player.on("error", this.error);
    this.player.on("pause", this.pause);
    this.player.on("play", this.play);
    this.player.on("useractive", this.useractive);
    this.player.on("userinactive", this.userinactive);
    window.addEventListener("beforeunload", ()=>{
      this.pushStats();
      return;
    });
    this.apiEventListener();
  },

  beforeDestroy() {
    if (this.player) {
      this.player.dispose();
      this.player = null;
    }
  },
  methods: {
    async initEpisode(episode){
      if (episode && episode.title){
        setDocumentTitle(episode.title,this.$store.state.setting.podcastTitle);
      }
      var tracks = this.player.remoteTextTracks().tracks_;
      for (var track of tracks){
        this.player.removeRemoteTextTrack(track);
      }

      if (episode.transcripts_url){
        let blobURL = await getTranscript(episode.transcripts_url);
        if (blobURL){
          this.player.addRemoteTextTrack({
            src: blobURL,
            srclang: 'en',
            label: 'english',
            kind: 'subtitles',//captions
            default:true,
          }, true);
        }
      }
    },
    loadeddata() {
      if (!this.startAtFix) {
        if (this.startAt > 0) {
          this.player.currentTime(this.startAt);
        }

        this.startAtFix = true;
      }
    },
    addCurrentTime(seconds) {
      if (this.player){
        this.player.currentTime(this.currentTime() + seconds);
      }
    },
    setCurrentTime(seconds) {
      if (this.player){
        let duration = this.player.duration();
        if(seconds > duration){
          this.player.pause();
        }else{
          this.player.currentTime(seconds);
          this.player.play();
        }
      }
    },
    postEvent(eventName) {
      let buffered = this.player.buffered();
      let duration = this.player.duration();
      let data = {};
      data.currentPosition = this.currentTime();
      if (duration > 0) {
        data.loadedProgress = buffered.end(buffered.length - 1) / duration;
        data.relativePosition = data.currentPosition / duration;
      } else {
        data.relativePosition = 0;
        data.loadedProgress = 0;
      }

      postEvent(eventName, data);
    },

    pushStats() {
      reportEngagement(this.episode.id,this.engagementStart, this.engagementEnd);
      this.engagementStart = this.engagementEnd;
    },

    currentTime(){
      return this.player?this.player.currentTime():0;
    },

    timeupdate() {
      if (!this.player || this.player.ended()){
        return;
      }
      if (this.engagementStart === null) {
        this.engagementStart = this.currentTime();
      }

      if (this.engagementEnd === null) {
        this.engagementEnd = this.currentTime();
      }

      if (Math.abs(this.currentTime() - this.engagementEnd) > 1) {
        //时间改变超过1s , 认为是seek了
        this.pushStats();
        this.engagementStart = this.currentTime();
      }

      this.engagementEnd = this.currentTime();
      this.postEvent("PB.Widget.Events.PLAY_PROGRESS");
    },
    progress() {
      this.postEvent("PB.Widget.Events.LOAD_PROGRESS");
    },
    playing() {
      this.postEvent("PB.Widget.Events.PLAY");

      this.$store.commit(MUTATION_UPDATE,["playerLoading",false]);
      this.$store.commit(MUTATION_UPDATE,["playerPlaying",true]);
      this.$store.commit(MUTATION_UPDATE,["playerLoaded",true]);
    },
    play() {
      this.$emit("play");
      this.player.playbackRate(this.playbackRate);
      this.$store.commit(MUTATION_UPDATE,["playing",true]);
    },
    pause($event) {
      this.$store.commit(MUTATION_UPDATE,["playerPlaying",false]);
      this.pushStats();
      this.$emit("pause", $event);
      this.postEvent("PB.Widget.Events.PAUSE");
      this.showPBAppAd();
      this.$store.commit(MUTATION_UPDATE,["playing",false]);
    },
    showPBAppAd() {
      if (!this.setting.pbad) {
        return;
      }
      if (this.setting.podcastProtectedType === "private") {
        return;
      }

      if (this.$store.state.showAppDownloadStatus !== 0) {
        return;
      }

      if (this.$store.state.isNonAppDownloadDialogOpen) {
        return;
      }

      if (this.player.error_){
        return;
      }
      if (this.player.scrubbing()) {
        return;
      }
      if (this.player.seeking()) {
        return;
      }
      if (this.player.ended() && this.$store.state.episodes.length > 1) {
        return;
      }
      this.$store.commit(MUTATION_UPDATE, ["showAppDownloadStatus", 1]);
    },
    showFutureWarning(){
      this.$toasted.show("Episode Available after " + this.episode.publishTime, {
        position: "top-center",
        duration : 2000
      });
    },
    async ended() {
      this.pushStats();
      this.postEvent("PB.Widget.Events.FINISH");

      if (this.$store.state.episodes.length>1){
        this.$store.dispatch(ACTION_NEXT_TRACK);
        await sleep(500);
        if (this.$store.state.current == 0){
          this.$store.commit(MUTATION_UPDATE,['start',this.$store.state.start+1]);
        }
      }else{
        this.showPBAppAd();
      }

    },
    seeked() {
      this.postEvent("PB.Widget.Events.SEEK");
    },

    apiEventListener() {
      let player = this.player;
      window.addEventListener("message", event => {
        if (
          typeof event.data === "object" &&
          event.data.action &&
          event.data.action.search("PB.Widget.API.") !== -1
        ) {
          try {
            var action = event.data.action.replace("PB.Widget.API.", "");
            switch (action) {
              case "PLAY":
                player.play();
                break;
              case "PAUSE":
                player.pause();
                break;
              case "NEXT":
                this.$store.dispatch(ACTION_NEXT_TRACK);
                break;
              case "PREV":
                this.$store.dispatch(ACTION_PREVIOUS_TRACK);
                break;
              case "TOGGLE":
                if (player.paused()) {
                  player.play();
                } else {
                  player.pause();
                }
                break;

              case "SEEK_TO":
                player.currentTime(event.data.value);
                break;
              case "SET_VOLUME":
                player.volume(event.data.value / 100.0);
                break;
              case "SKIP":
                // eslint-disable-next-line no-case-declarations
                const num = parseInt(event.data.value);
                if (num > 0 && num < this.$store.state.episodes.length) {
                  this.$store.commit(MUTATION_UPDATE, ["current", num]);
                }

                break;
              case "GET_VOLUME":
                postGetterCallback(action, player.volume() * 100);
                break;
              case "GET_DURATION":
                postGetterCallback(action, player.duration());
                break;
              case "GET_POSITION":
                postGetterCallback(action, player.currentTime());
                break;
              case "GET_PAUSED":
                postGetterCallback(action, player.paused());
                break;
              case "GET_SOURCES":
                postGetterCallback(
                  action,
                  this.$store.state.episodes.map(e => e.resource)
                );
                break;
              case "GET_CURRENTSOURCE":
                postGetterCallback(action, player.currentSrc());
                break;
              case "GET_CURRENTSOURCEINDEX":
                postGetterCallback(action, this.$store.state.current);
                break;
            }
          } catch (e) {
            // console.error(e);
          }
        }
      });
    },
    useractive() {
      this.$emit("useractive");
    },
    userinactive() {
      this.$emit("userinactive");
    },
    error(){
      if (this.retry>0 && this.episode.resource){
        this.retry --;
        this.player.src({src:this.episode.resource,type:this.episode.resourceMimetype});
        return false;
      }
      this.$store.commit(MUTATION_UPDATE,["playerError",true]);
      this.$store.commit(MUTATION_UPDATE,["playerLoading",false]);
    },
    waiting(){
      this.$store.commit(MUTATION_UPDATE,["playerLoading",true]);
    },
  }
};
</script>

<style scoped>
  .player-shadow{
    position:fixed;
    display:block;
    width:100%;
    height:100%;
    z-index:999999999
  }
</style>
